import * as React from 'react';
import { Alert, Box, Button, Chip, Link as MuiLink, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import {
    BETUP_FEED_LABELS, getBetupFeedChannelCategories, getBetupFeedChannels, getDashboardAppSetting, getPaginatedSports,
} from '../Api';
import { BetupFeedChannelCategoryDto, BetupFeedChannelDto } from '../models/dtos/BetupFeedChannelDto';
import SportEntityDto from '../models/dtos/SportEntityDto';
import { formatCount, formatFeedDate, loadErrorText } from '../components/betupFeed/BetupFeedShared';
import {
    categoryName, ChannelCategoriesDialog, ChannelDialog, channelLanguages, ChannelLogo, channelName,
    LanguageChips, languagesLabel, namedLanguagesCount, TopChannelChips,
} from '../components/betupFeed/BetupFeedChannelDialogs';

/** betup-server-api docs/feed-channels.md §5: seeded 1000. */
const TOP_MIN_SUBSCRIBERS_KEY = 'feed.channels.top_min_subscribers';
const TOP_MIN_SUBSCRIBERS_DEFAULT = '1,000';

type EditState = { channel: BetupFeedChannelDto | null } | null;

export default function BetupFeedChannelsPage() {
    const [rows, setRows] = React.useState<BetupFeedChannelDto[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [editing, setEditing] = React.useState<EditState>(null);
    const [topMinSubscribers, setTopMinSubscribers] = React.useState(TOP_MIN_SUBSCRIBERS_DEFAULT);
    const [categories, setCategories] = React.useState<BetupFeedChannelCategoryDto[]>([]);
    const [categoriesError, setCategoriesError] = React.useState<string | null>(null);
    const [categoriesOpen, setCategoriesOpen] = React.useState(false);
    /** null when the list failed to load. */
    const [sports, setSports] = React.useState<SportEntityDto[] | null>([]);


    const load = React.useCallback(async () => {
        setLoading(true);
        const channels = await getBetupFeedChannels();
        setLoading(false);
        if (channels == null) {
            setError(loadErrorText(BETUP_FEED_LABELS.channels, 'Failed to load channels.'));
            setRows([]);
            return;
        }
        setError(null);
        setRows(channels);
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const loadCategories = React.useCallback(async () => {
        const list = await getBetupFeedChannelCategories();
        if (list == null) {
            setCategoriesError(loadErrorText(BETUP_FEED_LABELS.channelCategories, 'Failed to load channel categories.'));
            return;
        }
        setCategoriesError(null);
        setCategories(list);
    }, []);

    React.useEffect(() => { loadCategories(); }, [loadCategories]);

    React.useEffect(() => {
        getPaginatedSports(0, 500).then(result => setSports(result?.items ?? null)).catch(() => setSports(null));
    }, []);

    React.useEffect(() => {
        getDashboardAppSetting(TOP_MIN_SUBSCRIBERS_KEY).then(setting => {
            const value = setting?.decimalValue ?? (setting?.stringValue != null ? Number(setting.stringValue) : null);
            if (value != null && Number.isFinite(value)) setTopMinSubscribers(value.toLocaleString('en-US'));
        });
    }, []);

    const onSaved = async (saved: BetupFeedChannelDto | null, created: boolean) => {
        setEditing(null);
        const name = saved != null ? channelName(saved) : 'Channel';
        setNotice(created
            ? `${name} created.`
            : `${name} saved.${saved?.top_pinned && !saved.is_top ? ' It becomes top on the next Crons run (within 30 minutes).' : ''}`);
        await load();
    };

    const categoryById = new Map(categories.map(c => [c.id, c]));
    const sportNameById = new Map((sports ?? []).map(s => [s.id, s.name]));

    const columns: GridColDef<BetupFeedChannelDto>[] = [
        {
            field: 'logo_url', headerName: '', width: 64, sortable: false,
            renderCell: ({ row }) => <ChannelLogo channel={row} />,
        },
        {
            field: 'name', headerName: 'Name (en)', width: 200,
            valueGetter: ({ row }) => channelName(row),
            renderCell: ({ row }) => (
                <Stack sx={{ minWidth: 0 }}>
                    <MuiLink component={RouterLink} to={`/marketing/betup-feed/channels/${row.id}`} underline="hover"
                        variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{channelName(row)}</MuiLink>
                    <Typography variant="caption" color="text.secondary">
                        #{row.id} · name in {languagesLabel(namedLanguagesCount(row.names))}
                    </Typography>
                    {channelLanguages(row).length > 0
                        ? <Box sx={{ mt: 0.5 }}><LanguageChips languages={channelLanguages(row)} /></Box>
                        : <Typography variant="caption" color="warning.main">No post languages</Typography>}
                    {row.model && <Typography variant="caption" color="text.secondary">{row.model}</Typography>}
                </Stack>
            ),
        },
        {
            field: 'description', headerName: 'Description (en)', flex: 1, minWidth: 220, sortable: false,
            renderCell: ({ row }) => row.descriptions?.en
                ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.descriptions.en}</Typography>
                : <Typography variant="body2" color="text.secondary">—</Typography>,
        },
        {
            field: 'category_ids', headerName: 'Categories / sports', width: 200, sortable: false,
            renderCell: ({ row }) => {
                const chips = [
                    ...(row.category_ids ?? []).map(id => {
                        const category = categoryById.get(id);
                        return { key: `c${id}`, label: category ? categoryName(category) : `category #${id}`, sport: false };
                    }),
                    ...(row.sport_ids ?? []).map(id => ({ key: `s${id}`, label: sportNameById.get(id) ?? `sport #${id}`, sport: true })),
                ];
                if (chips.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
                return (
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {chips.map(c => (
                            <Chip key={c.key} size="small" label={c.label} color={c.sport ? 'info' : 'default'}
                                variant={c.sport ? 'outlined' : 'filled'} />
                        ))}
                    </Stack>
                );
            },
        },
        {
            field: 'active', headerName: 'Active', width: 100,
            renderCell: ({ row }) => row.active
                ? <Chip size="small" color="success" label="Active" />
                : <Chip size="small" variant="outlined" label="Inactive" />,
        },
        {
            field: 'is_top', headerName: 'Top', width: 140,
            renderCell: ({ row }) => <TopChannelChips channel={row} />,
        },
        {
            field: 'subscribers_count', headerName: 'Subscribers', type: 'number', width: 110,
            renderCell: ({ row }) => (
                <MuiLink component={RouterLink} to={`/marketing/betup-feed/channels/${row.id}/subscribers`} underline="hover">
                    {formatCount(row.subscribers_count)}
                </MuiLink>
            ),
        },
        {
            field: 'posts_count', headerName: 'Posts', type: 'number', width: 80,
            renderCell: ({ row }) => formatCount(row.posts_count),
        },
        {
            field: 'created_at', headerName: 'Created', width: 170,
            renderCell: ({ row }) => formatFeedDate(row.created_at),
        },
        {
            field: 'actions', headerName: 'Actions', width: 150, sortable: false,
            renderCell: ({ row }) => (
                <Stack direction="row" gap={1}>
                    <Button size="small" variant="contained" component={RouterLink}
                        to={`/marketing/betup-feed/channels/${row.id}`}>View</Button>
                    <Button size="small" variant="outlined" onClick={() => setEditing({ channel: row })}>Edit</Button>
                </Stack>
            ),
        },
    ];

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="h4" sx={{ flex: 1 }}>BETUP Feed — channels</Typography>
                <Button variant="outlined" onClick={() => setCategoriesOpen(true)}>Categories</Button>
                <Button variant="contained" onClick={() => setEditing({ channel: null })}>New channel</Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
                Channels are authors that are not players: they publish channel posts players can subscribe to
                (“Channels you subscribed to”). A channel is <b>top</b> when it is pinned here or has more than{' '}
                {topMinSubscribers} subscribers; top channels are listed first and fill everyone's “Most popular
                channels”. The Crons recount subscribers and recompute top every 30 minutes, so counts and the Top chip
                can lag behind by up to half an hour.
            </Typography>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <Box>
                <DataGrid
                    autoHeight
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => 'auto'}
                    loading={loading}
                    initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    sx={{ '& .MuiDataGrid-cell': { py: 1.5, alignItems: 'center' } }}
                    localeText={{ noRowsLabel: 'No channels yet' }}
                />
            </Box>
            <ChannelDialog
                open={editing != null}
                channel={editing?.channel ?? null}
                categories={categories}
                sports={sports}
                topMinSubscribers={topMinSubscribers}
                onClose={() => setEditing(null)}
                onSaved={onSaved}
            />
            <ChannelCategoriesDialog
                open={categoriesOpen}
                categories={categories}
                loadError={categoriesError}
                onClose={() => setCategoriesOpen(false)}
                onChanged={loadCategories}
            />
        </Stack>
    );
}
